import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type { GetCategoryDataFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { UseDefaultTrackingFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseDefaultTrackingFragmentFragmentDoc';
import { useHashParameters } from '@packages/modules/src/hooks/useHashParameters/useHashParameters';
import type {
  GtmEventPageNavigation,
  GtmEventProductView,
  GtmProduct,
} from '@packages/tracking/src/hooks/useTracking/types';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinDisplayProduct } from '@packages/tracking/src/types/events';
import { useSearchParams } from 'next/navigation';
import { getDeterminedAvailability } from '../../helper/getDeterminedAvailability';
import type { GetGlycerinDisplayProductPayloadProps } from '../../helper/getGlycerinDisplayProductPayload/getGlycerinDisplayProductPayload';
import { getGlycerinDisplayProductPayload } from '../../helper/getGlycerinDisplayProductPayload/getGlycerinDisplayProductPayload';
import { getPrice } from '../../helper/getPrice';
import { getServiceLayerIds } from '../../helper/getServiceLayerIds';
import { getSizeAndVariantFromDimensions } from '../../helper/getSizeAndVariantFromDimensions';

/* GraphQL */ `
  fragment UseDefaultTrackingFragment on Product {
    akl
    sku
    name
    orderNumber
    promotion
    marketcode
    brand {
      name
    }
    price {
      ...GetPriceFragment
    }
    availability {
      message
      ...GetDeterminedAvailabilityFragment
    }
    ...GetSizeAndVariantFromDimensionsFragment
    ...GetServiceLayerIdsFragment
  }
`;

export type GtmProductViewProps = {
  data: FragmentType<typeof UseDefaultTrackingFragmentFragmentDoc>;
  navq?: GtmProduct['category'];
  categoryPathAsString?: string;
};

export const useDefaultTracking = () => {
  const dispatchTracking = useTracking();
  const searchParams = useSearchParams();
  const ref = searchParams?.get('ref') || null;
  const categoryId = searchParams?.get('nav-c') || null;

  const { position } = useHashParameters();

  const trackGtmProductView = ({ data, navq, categoryPathAsString }: GtmProductViewProps) => {
    const unmaskedData = getFragmentData(UseDefaultTrackingFragmentFragmentDoc, data);

    const determinedAvailability = getDeterminedAvailability(unmaskedData.availability);
    const { size, variant } = getSizeAndVariantFromDimensions(unmaskedData);
    const { price, oldPrice } = getPrice(unmaskedData.price);

    dispatchTracking<GtmEventProductView>({
      ecommerce: {
        detail: {
          products: [
            {
              availability: determinedAvailability,
              brand: unmaskedData.brand?.name,
              category: navq ? `search:${navq}` : categoryPathAsString,
              dimension1: unmaskedData.availability.message,
              dimension2: determinedAvailability,
              dimension4: oldPrice,
              dimension5: unmaskedData.sku,
              dimension6: unmaskedData.akl,
              dimension8: unmaskedData.promotion || '',
              id: unmaskedData.orderNumber,
              marketcode: unmaskedData.marketcode || undefined,
              name: unmaskedData.name,
              oldPrice,
              position: position ? parseInt(position, 10) : 1,
              price,
              size,
              variant,
              activeServiceLayerIds: getServiceLayerIds(unmaskedData),
            },
          ],
        },
      },
      event: 'EECproductDetailView',
    });
  };

  const trackGlycerinDisplayProduct = (
    data: GetGlycerinDisplayProductPayloadProps['data'],
    categoryPath: NonNullable<GetCategoryDataFragmentFragment['breadCrumb']>,
  ) => {
    dispatchTracking<GTMEventGlycerinDisplayProduct>({
      DisplayProductData: getGlycerinDisplayProductPayload({
        categoryPath,
        data,
        categoryIdSearchParam: categoryId,
        refSearchParam: ref,
      }),
      event: 'DisplayProduct',
    });
  };

  const trackGtmPageNavigation = () => {
    dispatchTracking<GtmEventPageNavigation>({
      event: 'ga_event',
      eventAction: 'NavigationClick',
      eventCategory: 'pageNavigation',
      eventLabel: 'DetailviewNavigation',
    });
  };

  return {
    trackGtmProductView,
    trackGlycerinDisplayProduct,
    trackGtmPageNavigation,
  };
};
